import type {
  AdminContractCreateResponse,
  ContractCreateCodeItem,
  ContractCreateRootCodeFields,
  ExtractedHiringCodeItem,
} from '@/types/contracts';

function getNonEmptyString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function getHiringCode(fields: ContractCreateRootCodeFields): string | undefined {
  return getNonEmptyString(fields.hiringCode) ?? getNonEmptyString(fields.hiring_code);
}

function getShortUrl(fields: ContractCreateRootCodeFields): string | undefined {
  return getNonEmptyString(fields.shortUrl) ?? getNonEmptyString(fields.short_url);
}

function getClientName(fields: ContractCreateRootCodeFields): string | undefined {
  return getNonEmptyString(fields.clientName) ?? getNonEmptyString(fields.client_name);
}

function buildFallbackShortUrl(hiringCode: string): string {
  const origin = getNonEmptyString(globalThis?.window?.location?.origin);
  return origin ? `${origin}/contratacion/${hiringCode}` : `/contratacion/${hiringCode}`;
}

function normalizeCodeItem(fields: ContractCreateRootCodeFields): ExtractedHiringCodeItem | null {
  const hiringCode = getHiringCode(fields);
  if (!hiringCode) {
    return null;
  }

  return {
    hiringCode,
    shortUrl: getShortUrl(fields) ?? buildFallbackShortUrl(hiringCode),
    clientName: getClientName(fields),
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  return value as Record<string, unknown>;
}

function fromCodesArray(codes: unknown): ExtractedHiringCodeItem[] {
  if (!Array.isArray(codes) || codes.length === 0) {
    return [];
  }
  return codes
    .map((item) => normalizeCodeItem((asRecord(item) ?? {}) as ContractCreateCodeItem))
    .filter((item): item is ExtractedHiringCodeItem => item !== null);
}

function fromContractsArray(contracts: unknown): ExtractedHiringCodeItem[] {
  if (!Array.isArray(contracts) || contracts.length === 0) {
    return [];
  }
  return contracts
    .map((item) => normalizeCodeItem((asRecord(item) ?? {}) as ContractCreateRootCodeFields))
    .filter((item): item is ExtractedHiringCodeItem => item !== null);
}

/**
 * Extrae los códigos de contratación de respuestas individual o linked.
 * Prioriza response.codes[] y, si no existe, usa hiring_code/short_url en raíz.
 */
export function extractHiringCodes(
  response: AdminContractCreateResponse | Record<string, unknown> | null | undefined
): ExtractedHiringCodeItem[] {
  const root = asRecord(response);
  if (!root) {
    return [];
  }

  const payloadCandidates = [
    root,
    asRecord(root.data),
    asRecord(root.result),
    asRecord(root.payload),
  ].filter((item): item is Record<string, unknown> => item !== null);

  for (const payload of payloadCandidates) {
    const fromCodes = fromCodesArray(payload.codes);
    if (fromCodes.length > 0) {
      return fromCodes;
    }

    const fallbackRootCode = normalizeCodeItem(payload as ContractCreateRootCodeFields);
    if (fallbackRootCode) {
      return [fallbackRootCode];
    }

    const fromContracts = fromContractsArray(payload.contracts);
    if (fromContracts.length > 0) {
      return fromContracts;
    }

    const fallbackContract = normalizeCodeItem((asRecord(payload.contract) ?? {}) as ContractCreateRootCodeFields);
    if (fallbackContract) {
      return [fallbackContract];
    }
  }

  return [];
}
